import React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../../config/joineeFormConstants";
import { applyInputRule, getInputRule } from "../../utils/inputRules";
import iconInfo from "../../Assests/Images/joinee/form/icon-info.png";
import iconCalendar from "../../Assests/Images/joinee/form/icon-calendar.svg";

var labelSx = {
  fontFamily: JOINEE_FORM_FONT,
  fontSize: 14,
  fontWeight: 400,
  color: "#171A1C",
  mb: "6px",
  display: "block",
  lineHeight: 1.14,
};

var inputSx = {
  fontFamily: JOINEE_FORM_FONT,
  "& .MuiOutlinedInput-root": {
    bgcolor: "#FDFDFD",
    borderRadius: "6px",
    fontFamily: JOINEE_FORM_FONT,
    fontSize: 14,
    fontWeight: 400,
    boxSizing: "border-box",
    minHeight: "36px",
    height: "36px",
    padding: "0 10px",
    "& fieldset": { borderColor: "#C7CCD1" },
    "&:hover fieldset": { borderColor: JOINEE_FORM_COLORS.navy },
    "&.Mui-focused fieldset": { borderColor: JOINEE_FORM_COLORS.primary },
  },
  "& .MuiInputBase-input": {
    fontFamily: JOINEE_FORM_FONT,
    fontWeight: 400,
    color: "#171A1C",
    fontSize: 14,
    lineHeight: 1.14,
    padding: "10px 0 !important",
    height: "auto",
    boxSizing: "border-box",
  },
  "& .MuiSelect-select": {
    padding: "10px 0 !important",
    display: "flex",
    alignItems: "center",
    minHeight: "unset !important",
    height: "auto",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#848F9A",
    opacity: 1,
    fontWeight: 400,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: JOINEE_FORM_FONT,
    fontWeight: 400,
    marginLeft: 0,
    minHeight: 18,
  },
  "& .MuiInputAdornment-root": {
    marginLeft: "6px",
    height: "16px",
    maxHeight: "16px",
  },
  "& .MuiIconButton-root": {
    padding: 0,
    marginRight: 0,
  },
};

export function FormFieldLabel(props) {
  return (
    <Typography
      component="label"
      htmlFor={props.htmlFor}
      sx={Object.assign({}, labelSx, { fontWeight: "400 !important" })}
    >
      {props.children}
      {props.required ? (
        <Box component="span" sx={{ color: JOINEE_FORM_COLORS.error, ml: 0.25 }}>
          *
        </Box>
      ) : null}
      {props.hint ? (
        <Tooltip title={props.hint} arrow placement="top">
          <Box
            component="img"
            src={iconInfo}
            alt="info"
            sx={{
              width: 16,
              height: 16,
              ml: 0.75,
              verticalAlign: "middle",
              cursor: "help",
            }}
          />
        </Tooltip>
      ) : null}
    </Typography>
  );
}

export function FormSectionTitle(props) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 2,
        pb: 1,
        borderBottom: "1px solid " + JOINEE_FORM_COLORS.border,
        flexWrap: "wrap",
      }}
    >
      <Typography
        sx={{
          fontFamily: JOINEE_FORM_FONT,
          fontSize: 18,
          fontWeight: 700,
          lineHeight: "26px",
          color: "#171A1C",
          letterSpacing: 0,
        }}
      >
        {props.children}
      </Typography>
      {props.action || null}
    </Box>
  );
}

export function FormAddButton(props) {
  return (
    <Button
      type="button"
      variant="outlined"
      onClick={props.onClick}
      disabled={props.disabled}
      sx={{
        fontFamily: JOINEE_FORM_FONT,
        fontSize: 14,
        fontWeight: 400,
        textTransform: "none",
        color: JOINEE_FORM_COLORS.primary,
        borderColor: JOINEE_FORM_COLORS.primary,
        borderRadius: "6px",
        minHeight: 32,
        height: 32,
        px: "12px",
        pl: "10px",
        py: "6px",
        gap: "6px",
        bgcolor: "#FDFDFD",
        boxShadow: "none",
        "&:hover": {
          borderColor: JOINEE_FORM_COLORS.primary,
          bgcolor: "rgba(0, 17, 211, 0.04)",
          boxShadow: "none",
        },
      }}
    >
      {props.children}
    </Button>
  );
}

export function FormTextField(props) {
  var rule = getInputRule(props.rule);
  var inputProps = rule
    ? Object.assign(
        {
          maxLength:
            rule.nativeMaxLength === false ? undefined : rule.maxLength,
          inputMode: rule.inputMode,
        },
        props.inputProps
      )
    : props.inputProps;

  function handleChange(event) {
    if (rule && event && event.target) {
      var next = applyInputRule(props.rule, event.target.value);
      if (next !== event.target.value) {
        event.target.value = next;
      }
    }
    if (props.onChange) {
      props.onChange(event);
    }
  }

  return (
    <Box sx={{ width: "100%", mb: props.mb !== undefined ? props.mb : 0 }}>
      {props.label ? (
        <FormFieldLabel
          htmlFor={props.id}
          required={props.required}
          hint={props.hint}
        >
          {props.label}
        </FormFieldLabel>
      ) : null}
      <TextField
        id={props.id}
        name={props.name}
        value={props.value == null ? "" : props.value}
        onChange={handleChange}
        onBlur={props.onBlur}
        placeholder={
          props.placeholder || (rule && rule.placeholder) || undefined
        }
        error={Boolean(props.error)}
        helperText={props.error || " "}
        fullWidth
        disabled={props.disabled}
        multiline={props.multiline}
        rows={props.rows}
        type={props.type || (rule && rule.type) || "text"}
        inputProps={inputProps}
        InputProps={props.InputProps}
        sx={
          props.multiline
            ? Object.assign({}, inputSx, {
                "& .MuiOutlinedInput-root": Object.assign(
                  {},
                  inputSx["& .MuiOutlinedInput-root"],
                  { height: "auto", minHeight: 36 }
                ),
              })
            : inputSx
        }
      />
    </Box>
  );
}

export function FormSelectField(props) {
  return (
    <Box sx={{ width: "100%" }}>
      {props.label ? (
        <FormFieldLabel htmlFor={props.id} required={props.required}>
          {props.label}
        </FormFieldLabel>
      ) : null}
      <TextField
        id={props.id}
        name={props.name}
        select
        value={props.value == null ? "" : props.value}
        onChange={props.onChange}
        onBlur={props.onBlur}
        error={Boolean(props.error)}
        helperText={props.error || " "}
        fullWidth
        disabled={props.disabled}
        SelectProps={{
          displayEmpty: true,
          renderValue: function (selected) {
            if (!selected) {
              return (
                <Box
                  component="span"
                  sx={{ color: JOINEE_FORM_COLORS.placeholder }}
                >
                  {props.placeholder || "Select"}
                </Box>
              );
            }
            var match = (props.options || []).find(function (opt) {
              return String(opt.value) === String(selected);
            });
            return match ? match.label : selected;
          },
        }}
        sx={inputSx}
      >
        {(props.options || []).map(function (opt) {
          return (
            <MenuItem
              key={String(opt.value)}
              value={opt.value}
              disabled={opt.disabled}
              sx={{ fontFamily: JOINEE_FORM_FONT, fontWeight: 400 }}
            >
              {opt.label}
            </MenuItem>
          );
        })}
      </TextField>
    </Box>
  );
}

export function FormRadioField(props) {
  return (
    <Box sx={{ width: "100%", mb: 1 }}>
      {props.label ? (
        <FormFieldLabel required={props.required}>{props.label}</FormFieldLabel>
      ) : null}
      <RadioGroup
        row
        name={props.name}
        value={props.value == null ? "" : props.value}
        onChange={props.onChange}
        sx={{
          flexWrap: "wrap",
          columnGap: { xs: 1.5, sm: 2 },
          rowGap: 0.25,
          alignItems: "center",
        }}
      >
        {(props.options || []).map(function (opt) {
          return (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              disabled={props.disabled}
              sx={{
                m: 0,
                mr: 0,
                ml: 0,
                "& .MuiFormControlLabel-label": { pl: 0.25 },
              }}
              control={
                <Radio
                  size="small"
                  disabled={props.disabled}
                  sx={{
                    p: "4px",
                    color: JOINEE_FORM_COLORS.border,
                    "&.Mui-checked": { color: JOINEE_FORM_COLORS.primary },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                    color: JOINEE_FORM_COLORS.label,
                    lineHeight: 1.3,
                  }}
                >
                  {opt.label}
                </Typography>
              }
            />
          );
        })}
      </RadioGroup>
      {props.error ? (
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 12,
            color: JOINEE_FORM_COLORS.error,
            mt: 0.5,
          }}
        >
          {props.error}
        </Typography>
      ) : null}
    </Box>
  );
}

export function FormCheckboxField(props) {
  var alignTop = Boolean(props.alignTop || props.legal);
  return (
    <FormControlLabel
      disabled={props.disabled}
      control={
        <Checkbox
          name={props.name}
          checked={Boolean(props.checked)}
          onChange={props.onChange}
          disabled={props.disabled}
          size="small"
          sx={{
            p: "4px",
            color: JOINEE_FORM_COLORS.border,
            "&.Mui-checked": { color: JOINEE_FORM_COLORS.primary },
            alignSelf: alignTop ? "flex-start" : "center",
          }}
        />
      }
      label={
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: props.dense ? 14 : 16,
            fontWeight: 400,
            fontStyle: "normal",
            color: props.dense ? "#2E3338" : "#171A1C",
            lineHeight: props.dense ? 1.35 : "24px",
            alignSelf: alignTop ? "stretch" : "center",
          }}
        >
          {props.label}
        </Typography>
      }
      sx={{
        alignItems: alignTop ? "flex-start" : "center",
        m: 0,
        gap: "8px",
        width: "100%",
        alignSelf: "stretch",
        "& .MuiFormControlLabel-label": {
          mt: 0,
          flex: "none",
          alignSelf: alignTop ? "stretch" : "center",
        },
      }}
    />
  );
}

export function FormDateField(props) {
  var value = props.value ? dayjs(props.value) : null;
  var hasValue = value && value.isValid();
  var [open, setOpen] = React.useState(false);
  var [anchorEl, setAnchorEl] = React.useState(null);
  var anchorRef = React.useRef(null);
  var placeholder = props.placeholder || "Select date";
  var display = hasValue ? value.format("DD MMM YYYY") : "";
  var compact = Boolean(props.compact);
  var fieldHeight = compact ? "24px" : "36px";

  function openPicker() {
    if (props.disabled) {
      return;
    }
    setAnchorEl(anchorRef.current);
    setOpen(true);
  }

  function closePicker() {
    setOpen(false);
    setAnchorEl(null);
  }

  return (
    <Box
      sx={{
        width: compact ? "auto" : "100%",
        display: compact ? "inline-flex" : "block",
        verticalAlign: compact ? "middle" : undefined,
        position: "relative",
      }}
    >
      {props.label ? (
        <FormFieldLabel required={props.required}>{props.label}</FormFieldLabel>
      ) : null}

      <Box
        ref={anchorRef}
        id={props.id}
        role="button"
        tabIndex={props.disabled ? -1 : 0}
        aria-label={props.label || placeholder}
        onClick={openPicker}
        onKeyDown={function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onBlur={props.onBlur}
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: compact ? "0 8px" : "10px",
          gap: "6px",
          width: compact ? "148px" : "100%",
          height: fieldHeight,
          minHeight: fieldHeight,
          maxHeight: fieldHeight,
          bgcolor: "#FDFDFD",
          border:
            "1px solid " + (open ? JOINEE_FORM_COLORS.primary : "#C7CCD1"),
          borderRadius: "6px",
          cursor: props.disabled ? "not-allowed" : "pointer",
          opacity: props.disabled ? 0.6 : 1,
          "&:hover": {
            borderColor: props.disabled
              ? "#C7CCD1"
              : open
              ? JOINEE_FORM_COLORS.primary
              : JOINEE_FORM_COLORS.navy,
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 400,
            fontSize: compact ? 12 : 14,
            lineHeight: 1.14,
            color: hasValue ? "#171A1C" : "#848F9A",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {display || placeholder}
        </Typography>
        <Box
          component="img"
          src={iconCalendar}
          alt=""
          sx={{
            width: compact ? 14 : 16,
            height: compact ? 14 : 16,
            display: "block",
            flexShrink: 0,
          }}
        />
      </Box>

      {props.error ? (
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 400,
            fontSize: 12,
            color: JOINEE_FORM_COLORS.error,
            mt: compact ? 0 : 0.5,
            minHeight: compact ? 0 : 18,
          }}
        >
          {props.error}
        </Typography>
      ) : compact ? null : (
        <Box sx={{ minHeight: 18 }} />
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          open={open}
          onOpen={openPicker}
          onClose={closePicker}
          value={hasValue ? value : null}
          onChange={function (date) {
            if (props.onChange) {
              props.onChange(
                date && date.isValid() ? date.toISOString() : null
              );
            }
          }}
          disableFuture={props.disableFuture}
          disablePast={props.disablePast}
          maxDate={props.maxDate ? dayjs(props.maxDate) : undefined}
          minDate={props.minDate ? dayjs(props.minDate) : undefined}
          format="DD MMM YYYY"
          slotProps={{
            textField: {
              sx: {
                position: "absolute",
                width: 0,
                height: 0,
                opacity: 0,
                overflow: "hidden",
                pointerEvents: "none",
                p: 0,
                m: 0,
              },
            },
            popper: {
              anchorEl: anchorEl,
              placement: "bottom-start",
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
}

export function FormTwoCol(props) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: props.single ? "1fr" : "1fr 1fr",
        },
        gap: { xs: 1.5, md: 2.5 },
        mb: props.mb !== undefined ? props.mb : 0.5,
      }}
    >
      {props.children}
    </Box>
  );
}
